import { Link } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Award, HeartHandshake, Truck, PlayCircle } from "lucide-react";
import { API, IMG_LUMBA, IMG_KUDA, IMG_KATROL, IMG_TIMBANGAN, waList, imgSrc, imageRatioClass, imagePositionClass, SECTION_DEFS, BG_STYLES, LAYOUTS, mergeHomeSections } from "@/lib/api";
import WhatsAppPicker from "@/components/WhatsAppPicker";
import WaIcon from "@/components/WaIcon";
import HeroMediaSlider from "@/components/HeroMediaSlider";
import VideoPlayer from "@/components/VideoPlayer";
import ExpandableDescription from "@/components/ExpandableDescription";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { usePageMeta } from "@/lib/seo";
import { buildVideoSlides, videoGridClass } from "@/lib/videoGallery";

const FEATURE_ICONS = [Award, ShieldCheck, HeartHandshake, Truck];
const DEFAULT_FEATURES = [
  { title: "Supplier & Distributor Tunggal Resmi", desc: "Tunggal untuk Tali PP LUMBA LUMBA & KUDA EMAS, Katrol LUMBA LUMBA, dan Timbangan Manual & Digital." },
  { title: "Kualitas Sempurna", desc: "Setiap produk melewati seleksi kualitas ketat sebelum sampai ke tangan pelanggan." },
  { title: "Kepuasan Pelanggan", desc: "Prioritas kami adalah pelayanan responsif dan solusi terbaik untuk setiap kebutuhan Anda." },
  { title: "Seluruh Indonesia", desc: "Melayani pengiriman ke seluruh wilayah Indonesia." },
];

const CAT_DEFAULTS = [
  { key: "tali", title: "Tali Tambang PP", desc: "LUMBA LUMBA & KUDA EMAS — kuat, tahan cuaca, mengapung di air.", img: IMG_LUMBA },
  { key: "katrol", title: "Katrol", desc: "Katrol LUMBA LUMBA dengan hook pengaman untuk angkat beban lebih ringan.", img: IMG_KATROL },
  { key: "timbangan", title: "Timbangan", desc: "Manual & digital dengan akurasi tinggi untuk toko hingga industri.", img: IMG_TIMBANGAN },
];

const BRAND_DEFAULTS = [
  { name: "LUMBA LUMBA", img: IMG_LUMBA, desc: "Tali PP & Katrol LUMBA LUMBA — Made in Indonesia, trusted quality untuk perkapalan, perikanan, dan industri." },
  { name: "KUDA EMAS", img: IMG_KUDA, desc: "Tali PP KUDA EMAS — lentur, kuat, dan ekonomis untuk kebutuhan pengikatan skala besar." },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [products, setProducts] = useState([]);
  const [site, setSite] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  usePageMeta(
    "Nawadah by NDH Official | Supplier & Distributor Industri",
    "Nawadah by NDH Official adalah supplier dan distributor tunggal di Indonesia untuk perlengkapan industri, perkakas, Tali PP LUMBA LUMBA & KUDA EMAS, katrol, dan timbangan.",
    { absoluteTitle: true },
  );

  useEffect(() => {
    API.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    API.get("/blog").then((r) => setBlogs((r.data || []).slice(0, 9))).catch(() => {});
    API.get("/videos").then((r) => setVideos(r.data || [])).catch(() => {});
    API.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    API.get("/settings").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const hc = site?.home_content || {};
  const waContacts = waList(site);
  const motif = BG_STYLES.find((b) => b.v === site?.bg_style);
  const bg = (def) => (motif && motif.v !== "maritime" ? motif.cls : def);
  const layoutDef = LAYOUTS.find((l) => l.v === site?.home_layout) || LAYOUTS[0];
  const pad = layoutDef.hero === "compact" ? "py-16" : "py-24";

  const features = (hc.features?.length ? hc.features : DEFAULT_FEATURES).slice(0, 4);
  const categorySource = hc.categories?.length ? hc.categories : CAT_DEFAULTS;
  const cats = categorySource.map((ov, i) => {
    const fallback = CAT_DEFAULTS[i] || {};
    const key = ov.key || fallback.key || `kategori-${i + 1}`;
    const live = products.find((p) => p.category === key && (p.images || []).length);
    return {
      key,
      title: ov.title || fallback.title || `Kategori ${i + 1}`,
      desc: ov.desc ?? fallback.desc ?? "",
      img: ov.image ? imgSrc(ov.image) : live ? imgSrc(live.images[0]) : fallback.img || IMG_LUMBA,
      imageRatio: ov.image_ratio || live?.image_ratios?.[0] || "4:3",
      imagePosition: ov.image_position || live?.image_positions?.[0] || "center",
    };
  });
  const brandSource = hc.brands?.length ? hc.brands : BRAND_DEFAULTS;
  const brands = brandSource.map((ov, i) => {
    const fallback = BRAND_DEFAULTS[i] || {};
    return { id: ov.id || `brand-${i}`, name: ov.name || fallback.name || `Merek ${i + 1}`, desc: ov.desc ?? fallback.desc ?? "", img: ov.image ? imgSrc(ov.image) : fallback.img || IMG_LUMBA, imageRatio: ov.image_ratio || "16:9" };
  });
  const blogSlides = Array.from({ length: Math.ceil(blogs.length / 3) }, (_, index) => blogs.slice(index * 3, index * 3 + 3));
  const brandSlides = Array.from({ length: Math.ceil(brands.length / 3) }, (_, index) => brands.slice(index * 3, index * 3 + 3));
  const { slides: videoSlides, itemsPerSlide: videoItemsPerSlide } = buildVideoSlides(videos, hc);
  const layout = (() => {
    if (site?.home_sections?.length) return mergeHomeSections(site.home_sections).filter((s) => s.visible !== false);
    if (layoutDef.order.length) return layoutDef.order.map((k) => SECTION_DEFS.find((d) => d.key === k)).filter(Boolean);
    return SECTION_DEFS;
  })();

  const sec = {
    features: (
      <section className={`relative ${pad} ${bg("bg-center-glow-mesh border-t border-white/5")}`} data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Mengapa NAWADAH by NDH Official</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-14">Standar Kualitas Tanpa Kompromi</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => {
              const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
              return (
                <div key={i} data-testid={`feature-${i}`} className="card-maritime-surface rounded-md p-8 hover:-translate-y-1 hover:border-gold/50 transition-transform">
                  <Icon className="h-8 w-8 text-gold mb-5" />
                  <h3 className="font-bold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    ),
    categories: (
      <section className={`relative ${pad} ${bg("bg-navy-dark bg-rope-pattern noise-overlay")}`} data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-14">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Produk Kami</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight">Kategori Produk Pilihan</h2>
            </div>
            <Link to="/produk" data-testid="categories-see-all" className="text-sm font-semibold text-gold hover:text-[#E6BE3A] inline-flex items-center gap-1 transition-colors">
              Lihat Semua <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cats.map((c, i) => (
              <Link key={c.key || i} to="/produk" data-testid={`category-card-${i}`} className="group relative overflow-hidden rounded-md border border-white/10 hover:-translate-y-1 transition-transform">
                <div className={`${imageRatioClass(c.imageRatio, "4:3")} overflow-hidden`}>
                  <img src={c.img} alt={c.title} className={`w-full h-full object-cover ${imagePositionClass(c.imagePosition)} group-hover:scale-105 transition-transform duration-500`} />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#040F1D] via-[#040F1D]/40 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-display text-xl text-white mb-1">{c.title}</h3>
                  <p className="text-sm text-slate-300">{c.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    ),
    brands: (
      <section className={`relative ${pad} ${bg("bg-corner-glows border-y border-white/5")}`} data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">Merek Resmi</p>
            <h2 className="font-display text-2xl tracking-tight sm:text-3xl lg:text-4xl">Merek Unggulan Kami</h2>
          </div>
          <Carousel opts={{ align: "start", slidesToScroll: 1 }} className="pb-12" data-testid="home-brand-carousel">
            <CarouselContent>
              {brandSlides.map((slide, slideIndex) => (
                <CarouselItem key={slideIndex} data-testid={`home-brand-slide-${slideIndex}`}>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    {slide.map((b, itemIndex) => {
                      const index = slideIndex * 3 + itemIndex;
                      return (
                        <div key={b.id || index} data-testid={`brand-${index}`} className={`relative flex items-end overflow-hidden rounded-md border border-white/10 ${imageRatioClass(b.imageRatio, "16:9")}`}>
                          <img src={b.img} alt={b.name} className="absolute inset-0 h-full w-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#040F1D]/90 via-[#040F1D]/25 to-transparent" />
                          <div className="relative p-5 sm:p-6">
                            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Merek Unggulan</p>
                            <h3 className="font-display text-xl sm:text-2xl">{b.name}</h3>
                            {b.desc && <p className="mt-2 line-clamp-2 text-xs text-slate-300">{b.desc}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {brandSlides.length > 1 && (
              <>
                <CarouselPrevious className="-bottom-1 left-1/2 top-auto -translate-x-10 translate-y-0 border-gold/40 bg-[#07182A] text-gold hover:bg-gold hover:text-[#0B1F3A]" />
                <CarouselNext className="-bottom-1 right-1/2 top-auto translate-x-10 translate-y-0 border-gold/40 bg-[#07182A] text-gold hover:bg-gold hover:text-[#0B1F3A]" />
              </>
            )}
          </Carousel>
        </div>
      </section>
    ),
    testimonials: (
      <section className={`relative ${pad} ${bg("bg-navy-dark bg-steel-mesh noise-overlay")}`} data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Testimoni</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-14">Dipercaya Pelanggan Kami</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <figure key={t.id} data-testid={`testimonial-${t.id}`} className="card-maritime-surface rounded-md p-8 hover:-translate-y-1 transition-transform">
                <div className="text-gold text-sm mb-4">{"★".repeat(t.rating)}</div>
                {t.video_url && (
                  <div className="aspect-video bg-black rounded-md overflow-hidden mb-4" data-testid={`testimonial-video-${t.id}`}>
                    <VideoPlayer url={t.video_url} title={`Review ${t.name}`} className="h-full w-full object-cover" />
                  </div>
                )}
                <blockquote className="text-sm text-slate-300 leading-relaxed mb-6">"{t.content}"</blockquote>
                <figcaption>
                  <p className="font-bold text-sm text-white">{t.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    ),
    blog: (
      <section className={`relative ${pad} ${bg("bg-corner-glows border-y border-white/5")}`} data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-14">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Blog &amp; Artikel</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight">Panduan Produk untuk Anda</h2>
            </div>
            <Link to="/blog" data-testid="blog-see-all" className="text-sm font-semibold text-gold hover:text-[#E6BE3A] inline-flex items-center gap-1 transition-colors">
              Lihat Semua <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {blogs.length ? (
            <Carousel opts={{ align: "start", slidesToScroll: 1 }} className="pb-12" data-testid="home-blog-carousel">
              <CarouselContent>
                {blogSlides.map((slide, slideIndex) => (
                  <CarouselItem key={slideIndex} data-testid={`home-blog-slide-${slideIndex}`}>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                      {slide.map((b) => (
                        <Link key={b.slug} to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="card-maritime-surface group overflow-hidden rounded-md transition-transform hover:-translate-y-1 hover:border-gold/50">
                          <div className={`${imageRatioClass(b.image_ratio, "16:9")} overflow-hidden bg-white`}>
                            <img src={b.image ? imgSrc(b.image) : IMG_LUMBA} alt={b.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          </div>
                          <div className="p-5">
                            <h3 className="mb-2 line-clamp-2 text-sm font-bold leading-snug">{b.title}</h3>
                            <p className="line-clamp-2 text-xs text-slate-400">{b.excerpt}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {blogSlides.length > 1 && (
                <>
                  <CarouselPrevious className="-bottom-1 left-1/2 top-auto -translate-x-10 translate-y-0 border-gold/40 bg-[#07182A] text-gold hover:bg-gold hover:text-[#0B1F3A]" />
                  <CarouselNext className="-bottom-1 right-1/2 top-auto translate-x-10 translate-y-0 border-gold/40 bg-[#07182A] text-gold hover:bg-gold hover:text-[#0B1F3A]" />
                </>
              )}
            </Carousel>
          ) : (
            <div className="rounded-md border border-dashed border-white/15 px-6 py-10 text-center text-sm text-slate-400">Artikel akan segera hadir.</div>
          )}
        </div>
      </section>
    ),
    videos: (
      <section className={`relative ${pad} ${bg("bg-navy-dark bg-steel-mesh border-y border-white/5")}`} data-testid="videos-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-14 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">Galeri Video</p>
              <h2 className="font-display text-2xl tracking-tight sm:text-3xl lg:text-4xl">Lihat NAWADAH Lebih Dekat</h2>
            </div>
            <Link to="/video" data-testid="videos-see-all" className="inline-flex items-center gap-1 text-sm font-semibold text-gold transition-colors hover:text-[#E6BE3A]">Lihat Semua <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {videos.length ? (
            <Carousel opts={{ align: "start", slidesToScroll: 1 }} className="pb-12" data-testid="home-video-carousel">
              <CarouselContent>
                {videoSlides.map((slide, slideIndex) => (
                  <CarouselItem key={slideIndex} data-testid={`home-video-slide-${slideIndex}`}>
                    <div className={`grid gap-6 ${videoGridClass(videoItemsPerSlide)}`}>
                      {slide.map((item) => (
                        <article key={item.id} className="card-maritime-surface overflow-hidden rounded-md" data-testid={`home-video-${item.id}`}>
                          <div className="aspect-video bg-black"><VideoPlayer url={item.video_url} title={item.title} poster={item.thumbnail_url ? imgSrc(item.thumbnail_url) : ""} className="h-full w-full object-cover" /></div>
                          <div className="p-5">
                            <p className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-gold"><PlayCircle className="h-3.5 w-3.5" /> Video NAWADAH</p>
                            <h3 className="font-bold leading-snug">{item.title}</h3>
                            <ExpandableDescription
                              text={item.description}
                              className="mt-2 text-xs leading-relaxed text-slate-400"
                            />
                          </div>
                        </article>
                      ))}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {videoSlides.length > 1 && (
                <>
                  <CarouselPrevious className="-bottom-1 left-1/2 top-auto -translate-x-10 translate-y-0 border-gold/40 bg-[#07182A] text-gold hover:bg-gold hover:text-[#0B1F3A]" />
                  <CarouselNext className="-bottom-1 right-1/2 top-auto translate-x-10 translate-y-0 border-gold/40 bg-[#07182A] text-gold hover:bg-gold hover:text-[#0B1F3A]" />
                </>
              )}
            </Carousel>
          ) : (
            <div className="rounded-md border border-dashed border-white/15 px-6 py-10 text-center text-sm text-slate-400">Video akan segera hadir.</div>
          )}
        </div>
      </section>
    ),
    cta: (
      <section className={`relative overflow-hidden ${pad} ${bg("bg-cta-spotlight border-y border-gold/30 noise-overlay")}`} data-testid="cta-section">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">{hc.cta_title || "Siap Berbelanja atau Bertanya Dulu?"}</h2>
            <p className="text-slate-300 mt-3 text-sm sm:text-base max-w-xl">
              {hc.cta_subtitle || "Jelajahi katalog produk kami, atau hubungi langsung via WhatsApp untuk menanyakan harga & ketersediaan stok. Akun pelanggan dibuatkan oleh pemilik."}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/produk" data-testid="cta-register" className="px-8 py-3.5 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors">
              Lihat Katalog
            </Link>
            <button onClick={() => setWaOpen(true)} data-testid="cta-wa" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-white/30 text-white font-bold text-sm rounded-md hover:border-gold hover:text-gold transition-colors">
              <WaIcon className="h-4 w-4 text-[#25D366]" /> Chat WhatsApp
            </button>
          </div>
        </div>
      </section>
    ),
  };

  const heroImg = hc.hero_image ? imgSrc(hc.hero_image) : IMG_LUMBA;
  const heroSlides = Array.isArray(hc.hero_slides) ? hc.hero_slides : [];
  const centered = layoutDef.hero === "center";
  const heroText = (
    <>
      <p className="fade-in-up text-xs font-bold uppercase tracking-[0.3em] text-gold mb-6" data-testid="hero-label">
        {hc.hero_label || "Supplier & Distributor Tunggal Resmi — Indonesia"}
      </p>
      <h1 className={`fade-in-up stagger-1 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl ${centered ? "mx-auto" : ""}`} data-testid="hero-title">
        {hc.hero_title || "Tali Tambang, Katrol & Timbangan"} <span className="text-gold italic">{hc.hero_highlight || "Berkualitas Sempurna"}</span>
      </h1>
      <p className={`fade-in-up stagger-2 mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed ${centered ? "mx-auto" : ""}`} data-testid="hero-desc">
        {hc.hero_desc || "NAWADAH by NDH Official — supplier & distributor tunggal Tali PP LUMBA LUMBA & KUDA EMAS, Katrol LUMBA LUMBA, dan Timbangan Manual & Digital. Kepuasan pelanggan adalah prioritas kami."}
      </p>
      <div className={`fade-in-up stagger-3 mt-10 flex flex-wrap gap-4 ${centered ? "justify-center" : ""}`}>
        <Link to="/produk" data-testid="hero-cta-catalog" className="inline-flex items-center gap-2 px-8 py-3.5 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors">
          Lihat Katalog Produk <ArrowRight className="h-4 w-4" />
        </Link>
        <button onClick={() => setWaOpen(true)} data-testid="hero-cta-wa" className="inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 text-white font-semibold text-sm rounded-md hover:border-gold hover:text-gold transition-colors">
          <WaIcon className="h-4 w-4 text-[#25D366]" /> Tanya Harga via WhatsApp
        </button>
      </div>
    </>
  );

  return (
    <div className="bg-navy-dark text-white">
      {/* Hero */}
      {layoutDef.hero === "split" ? (
        <section className="relative py-24 noise-overlay overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0 bg-maritime-grid" />
          <div className="absolute inset-0 vignette-overlay" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>{heroText}</div>
            <div className="relative fade-in-up stagger-2">
              <HeroMediaSlider slides={heroSlides} fallbackUrl={heroImg} className={`w-full rounded-md border border-gold/30 ${imageRatioClass(hc.hero_image_ratio, "4:3")}`} />
              <div className="absolute -inset-3 border border-gold/20 rounded-md pointer-events-none" />
            </div>
          </div>
        </section>
      ) : (
        <section className={`relative ${layoutDef.hero === "compact" ? "min-h-[58vh]" : hc.hero_image_ratio ? `min-h-[680px] sm:min-h-[620px] lg:min-h-0 ${imageRatioClass(hc.hero_image_ratio, "4:3")}` : "min-h-[88vh]"} flex items-center noise-overlay`} data-testid="hero-section">
          <HeroMediaSlider slides={heroSlides} fallbackUrl={heroImg} className="absolute inset-0 h-full w-full" background />
          <div className="absolute inset-0 bg-[#040F1D]/75" />
          <div className="absolute inset-0 bg-maritime-grid" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 85% 15%, rgba(201,162,39,0.16) 0%, transparent 50%)" }} />
          <div className="absolute inset-0 vignette-overlay" />
          <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 ${layoutDef.hero === "compact" ? "py-16" : "py-24"} w-full ${centered ? "text-center" : ""}`}>
            {heroText}
          </div>
        </section>
      )}

      {layout.map((s) => (
        <Fragment key={s.key}>{sec[s.key]}</Fragment>
      ))}

      <WhatsAppPicker
        open={waOpen}
        onClose={() => setWaOpen(false)}
        contacts={waContacts}
        message="Halo Admin NAWADAH, saya ingin bertanya mengenai produk dan harga."
      />
    </div>
  );
}
